// The location form and what a location document stores. Pure and import-free, same rule as the
// other helpers under organigram/: the spec loads this directly, without the DI / Firebase chain.
//
// This is the organigram page's OWN editor. It carries the same fields as the legacy entity modal's
// location form (entity-modal.component.ts:67-72, :691-702) but the legacy component is not opened
// - that form validates the name and nothing else, which is what let unusable locations be saved.
//
// Why the validation matters more here than it looks: a node does not store a location REFERENCE,
// it stores a copy of the address (toBackendLocation in organigram-locations.ts). A physical
// location with no city projects to null, so picking it gives the node no location at all - the
// save succeeds and nothing reaches the backend. And until the backend carries a location id, a
// Cloud Function has to match nodes back to a location BY ADDRESS, so two sites that differ only in
// a field left blank are indistinguishable. Both failures are silent, so they are refused here.

export type OrgLocationType = 'PHYSICAL' | 'ONLINE';

export interface LocationForm {
  name: string;
  type: OrgLocationType;
  address: string;
  houseNumber: string;
  address2: string;
  city: string;
  zipCode: string;
  country: string;
  timezone: string;
}

// The default timezone the legacy form used. Kept so a location saved here and one saved there
// agree, rather than one of them carrying an empty timezone the map then cannot place.
export const DEFAULT_TIMEZONE = 'Europe/Berlin';

// The zones this business actually operates in. Two jobs: they lead the picker, so the list you
// see before typing is the one you probably want; and they ARE the list on a runtime with no
// Intl.supportedValuesOf, rather than an empty picker.
const COMMON_TIMEZONES = [
  'Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich', 'Europe/Paris', 'Europe/Amsterdam',
  'Europe/Brussels', 'Europe/Luxembourg', 'Europe/Madrid', 'Europe/Rome', 'Europe/Warsaw',
  'Europe/London', 'Europe/Lisbon', 'Europe/Istanbul',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Sao_Paulo',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Shanghai', 'Asia/Tokyo',
  'Australia/Sydney', 'UTC'
];

// Whether the runtime accepts a zone at all, which is a different question from whether it
// enumerates it (see timezoneOptions). A rejected zone throws a RangeError.
function isUsableZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

// The zones the picker offers, the common ones first. Taken from the platform when it can supply
// them - every current browser can - so the list is the real IANA set (400+) and never needs
// maintaining here.
//
// `current` is always included even when the platform does not list it. Without that, editing a
// location whose stored zone the runtime does not know would show the picker on some OTHER value
// and silently rewrite it on save, which is exactly the kind of quiet data loss a picker is
// supposed to prevent.
export function timezoneOptions(current?: string): string[] {
  // Not in TypeScript's lib typings for this target, hence the cast.
  const supported = (Intl as any)?.supportedValuesOf;
  let zones: string[];
  try {
    zones = typeof supported === 'function' ? supported.call(Intl, 'timeZone') : [];
  } catch {
    zones = [];   // a runtime that has the method but rejects 'timeZone'
  }
  if (!zones?.length) { zones = COMMON_TIMEZONES; }
  // Kept even when the platform does not ENUMERATE them. supportedValuesOf returns canonical ids
  // only: 'UTC' is absent from it everywhere, and Asia/Kolkata is listed under its old name
  // Asia/Calcutta. Both are still accepted wherever a timezone is used, so intersecting with the
  // platform list - which is what this did first - silently made UTC impossible to pick.
  const common = COMMON_TIMEZONES.filter(isUsableZone);
  const rest = zones.filter(zone => common.indexOf(zone) < 0);
  const ordered = [...common, ...rest];
  const value = (current || '').trim();
  if (value && ordered.indexOf(value) < 0) { return [value, ...ordered]; }
  return ordered;
}

// Memoised because building an Intl.DateTimeFormat is not free: measured at ~124ms for the full
// 418-zone list, which is a visible hitch on opening the dialog. Paid once per session.
const OFFSET_LABELS = new Map<string, string>();

// The zone's current offset, worded the way the platform words it: "GMT+2", "GMT+5:30", or plain
// "GMT" for UTC. '' when the runtime cannot place the zone, so the caller renders nothing rather
// than a broken label.
//
// DISPLAY ONLY, and only true right now - the offset shifts with daylight saving, so Europe/Berlin
// reads GMT+2 in August and GMT+1 in January. That is exactly why the stored value is the IANA id
// and never this label: the id survives the clocks changing, an offset does not.
export function zoneOffsetLabel(zone: string): string {
  if (!zone) { return ''; }
  const cached = OFFSET_LABELS.get(zone);
  if (cached !== undefined) { return cached; }
  let label = '';
  try {
    // `shortOffset` is ES2022 and every current engine has it, but this project's TypeScript lib
    // types timeZoneName as 'short' | 'long' only - hence the cast. It matters which one: 'short'
    // yields locale abbreviations like EDT for some zones, 'shortOffset' always yields GMT±H.
    const options = { timeZone: zone, timeZoneName: 'shortOffset' } as unknown as Intl.DateTimeFormatOptions;
    const part = new Intl.DateTimeFormat('en', options)
      .formatToParts(new Date())
      .find(p => p.type === 'timeZoneName');
    label = part ? part.value : '';
  } catch {
    label = '';
  }
  OFFSET_LABELS.set(zone, label);
  return label;
}

// Zone names are machine identifiers, so they are matched the way they READ rather than the way
// they are stored: separators become spaces, so "new york" finds America/New_York and "europe ber"
// finds Europe/Berlin. Without that, the underscore in the middle of a city name makes the obvious
// query return nothing.
function searchable(value: string): string {
  return (value || '').toLowerCase().replace(/[_\/]/g, ' ');
}

// Every zone matching the query. The picker renders all of them and scrolls - there is no cap, so
// nothing is reachable only by guessing at a narrower query. A blank query matches everything, so
// the picker opens on the full list (common zones first).
export function matchTimezones(query: string, zones: string[]): string[] {
  const q = searchable(query).trim();
  if (!q) { return (zones || []).slice(); }
  return (zones || []).filter(zone => searchable(zone).indexOf(q) >= 0);
}

export function blankLocationForm(): LocationForm {
  return {
    name: '', type: 'PHYSICAL', address: '', houseNumber: '', address2: '',
    city: '', zipCode: '', country: '', timezone: DEFAULT_TIMEZONE
  };
}

// Seeds the form from a stored location document, for editing. Every field lands as a string so no
// input is uncontrolled, and an unknown type falls back to PHYSICAL - the stricter of the two, so a
// malformed document is asked for an address rather than waved through as remote.
export function locationFormFrom(doc: any): LocationForm {
  const form = blankLocationForm();
  form.name = doc?.name || '';
  form.type = doc?.type === 'ONLINE' ? 'ONLINE' : 'PHYSICAL';
  form.address = doc?.address || '';
  form.houseNumber = doc?.houseNumber || '';
  form.address2 = doc?.address2 || '';
  form.city = doc?.city || '';
  form.zipCode = doc?.zipCode || '';
  form.country = doc?.country || '';
  form.timezone = doc?.timezone || DEFAULT_TIMEZONE;
  return form;
}

// Returns null when the form may be saved, otherwise the message shown under it.
//
// ONLINE needs nothing but a name: it projects to { remote: true } and carries no address at all.
// PHYSICAL needs a city (without one the whole projection is null) and a street (without one two
// sites in the same city are the same address, which is what the node-matching cannot separate).
export function validateLocationForm(form: Partial<LocationForm>): string | null {
  if (!(form?.name || '').trim()) { return 'A name is required.'; }
  if (form?.type === 'ONLINE') { return null; }
  if (!(form?.city || '').trim()) { return 'A city is required, or set the location to online.'; }
  if (!(form?.address || '').trim()) { return 'A street is required, so two sites in one city stay tellable apart.'; }
  return null;
}

// The document written to organigram_locations. Trimmed, and with the address fields CLEARED for an
// online location rather than carried over: a remote location with a leftover street reads as a
// physical one everywhere it is displayed, and would be geocoded to the office it no longer has.
export function locationSaveBody(form: Partial<LocationForm>): Record<string, string> {
  const text = (value: any) => (value || '').trim();
  const online = form?.type === 'ONLINE';
  return {
    name: text(form?.name),
    type: online ? 'ONLINE' : 'PHYSICAL',
    address: online ? '' : text(form?.address),
    houseNumber: online ? '' : text(form?.houseNumber),
    address2: online ? '' : text(form?.address2),
    city: online ? '' : text(form?.city),
    zipCode: online ? '' : text(form?.zipCode),
    country: online ? '' : text(form?.country),
    timezone: text(form?.timezone) || DEFAULT_TIMEZONE
  };
}
